/**
 * iter318 wave 15 — VyomOS Pricing (Spec §9, §47).
 * CMS-driven when Super Admin has published plans/addons/faqs;
 * falls back to the static wave-13 defaults otherwise.
 */
import { Link } from 'react-router-dom';
import { Check } from 'lucide-react';
import { useWebsitePricing } from '../../../hooks/useWebsiteContent';

const DEFAULT_PLANS = [
  {
    name: 'Starter',
    tagline: 'For single-location academies just getting organized',
    price_label: '$99',
    cadence: '/month',
    highlight: false,
    features: [
      'Up to 150 active participants',
      'CRM, registrations, and enrollments',
      'Class scheduling and attendance',
      'Payments (via Stripe) and invoices',
      'Basic reporting dashboards',
      'Email and in-app communication',
    ],
    cta_label: 'Start with Starter',
    cta_href: '/contact',
  },
  {
    name: 'Growth',
    tagline: 'For growing academies and multi-program operators',
    price_label: '$249',
    cadence: '/month',
    highlight: true,
    features: [
      'Up to 750 active participants',
      'Everything in Starter, plus:',
      'Facility & lane booking module',
      'Coach billing and shift scheduling',
      'White-label parent portal',
      'Automated reminders and follow-ups',
      'Priority email support',
    ],
    cta_label: 'Choose Growth',
    cta_href: '/contact',
  },
  {
    name: 'Scale',
    tagline: 'For multi-location facilities and complex operations',
    price_label: 'Custom',
    cadence: '',
    highlight: false,
    features: [
      'Unlimited participants and locations',
      'Everything in Growth, plus:',
      'Custom workflows and modules',
      'Dedicated success manager',
      'SSO and advanced permissions',
      'Custom domain + branded portal',
      'AI insights and forecasting',
    ],
    cta_label: 'Talk to Sales',
    cta_href: '/contact',
  },
];

const DEFAULT_ADDONS = [
  { title: 'Facility Booking Module', body: 'Advanced lane, court, room, and field scheduling with maintenance windows.' },
  { title: 'Tournament Suite', body: 'Registration, brackets, seeding, payments, and communication for events.' },
  { title: 'WhatsApp Business', body: 'Two-way parent conversations with templates and inbox routing.' },
  { title: 'Coach Payroll Automation', body: 'Time entries, class-based payments, and monthly disbursement.' },
];

const DEFAULT_FAQS = [
  { question: 'Do I need a Stripe account?', answer: 'You can start with Stripe standard connect during onboarding. Payments flow directly to your account — VyomOS never touches customer funds.' },
  { question: 'Can I switch plans later?', answer: 'Yes. Upgrade any time from Super Admin. Downgrades take effect at the next billing cycle.' },
  { question: 'Is there a free trial?', answer: 'Yes — every new tenant gets a 14-day full-feature trial. No credit card is required to start.' },
  { question: 'What about my existing data?', answer: 'Import spreadsheets during onboarding, or work with our team on a guided migration for larger datasets.' },
];

export default function VyomosPricing() {
  const { pricing, loading } = useWebsitePricing();
  if (loading && !pricing) {
    return <div className="min-h-[60vh]" data-testid="vyomos-pricing-loading" />;
  }
  const plans = (pricing?.plans && pricing.plans.length > 0) ? pricing.plans : DEFAULT_PLANS;
  const addons = (pricing?.addons && pricing.addons.length > 0) ? pricing.addons : DEFAULT_ADDONS;
  const faqs = (pricing?.faqs && pricing.faqs.length > 0) ? pricing.faqs : DEFAULT_FAQS;

  return (
    <div data-testid="vyomos-pricing">
      {/* Header */}
      <section className="bg-gradient-to-b from-[#EAF5FF] to-[#F8FBFF]">
        <div className="max-w-6xl mx-auto px-6 py-20 text-center">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Pricing</div>
          <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-4">
            Plans designed to grow with your operation.
          </h1>
          <p className="text-lg text-[#566579] max-w-2xl mx-auto">
            Start with what you need today. Expand modules and capacity as your programs, facilities, and teams grow.
          </p>
        </div>
      </section>

      {/* Plan tiers */}
      <section className="bg-white">
        <div className="max-w-6xl mx-auto px-6 pb-16 -mt-8">
          <div className="grid md:grid-cols-3 gap-6">
            {plans.map((p) => (
              <div
                key={p.name}
                data-testid={`pricing-plan-${p.name.toLowerCase()}`}
                className={
                  p.highlight
                    ? 'relative rounded-3xl border-2 border-[#1677FF] bg-white p-8 shadow-xl'
                    : 'relative rounded-3xl border border-[#DCE6F0] bg-white p-8 shadow-sm hover:shadow-md transition'
                }
              >
                {p.highlight && (
                  <div className="absolute -top-3 left-8 bg-[#1677FF] text-white text-xs font-semibold px-3 py-1 rounded-full">
                    Most popular
                  </div>
                )}
                <h3 className="text-xl font-bold mb-1">{p.name}</h3>
                <p className="text-sm text-[#566579] mb-6 min-h-[40px]">{p.tagline || p.tag}</p>
                <div className="mb-6">
                  <span className="text-4xl font-bold">{p.price_label || p.price}</span>
                  <span className="text-[#566579] ml-1">{p.cadence || ''}</span>
                </div>
                <ul className="space-y-3 mb-8 min-h-[220px]">
                  {(p.features || []).map((f) => (
                    <li key={f} className="flex items-start gap-2 text-sm">
                      <Check className="h-4 w-4 text-[#1677FF] mt-0.5 flex-shrink-0" />
                      <span className="text-[#10233F]">{f}</span>
                    </li>
                  ))}
                </ul>
                <Link
                  to={p.cta_href || '/contact'}
                  data-testid={`pricing-cta-${p.name.toLowerCase()}`}
                  className={
                    p.highlight
                      ? 'block w-full text-center py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold transition'
                      : 'block w-full text-center py-3 rounded-full border border-[#DCE6F0] hover:border-[#55AEEF] text-[#10233F] font-semibold transition'
                  }
                >
                  {p.cta_label || p.cta || 'Get started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Add-ons */}
      <section className="bg-[#EAF5FF]">
        <div className="max-w-6xl mx-auto px-6 py-16">
          <div className="text-xs uppercase tracking-[0.15em] font-semibold text-[#1677FF] mb-3">Add-ons</div>
          <h2 className="text-3xl font-bold tracking-tight mb-3">Add capabilities only when you need them.</h2>
          <p className="text-[#566579] mb-8 max-w-2xl">
            Every plan includes the core operating system. Optional modules extend it for specialized workflows.
          </p>
          <div className="grid md:grid-cols-2 gap-4">
            {addons.map((a) => (
              <div key={a.id || a.title} className="bg-white border border-[#DCE6F0] rounded-2xl p-6">
                <h3 className="font-semibold mb-2">{a.title}</h3>
                <p className="text-sm text-[#566579] leading-relaxed">{a.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-white">
        <div className="max-w-4xl mx-auto px-6 py-16">
          <h2 className="text-3xl font-bold tracking-tight mb-8 text-center">Common questions</h2>
          <div className="space-y-4">
            {faqs.map((f) => {
              const q = f.question;
              const a = f.answer;
              return (
                <details
                  key={f.id || q}
                  data-testid={`faq-${q.slice(0, 20).replace(/\s/g, '-').toLowerCase()}`}
                  className="group bg-[#F8FBFF] border border-[#DCE6F0] rounded-2xl px-6 py-4 open:bg-white transition"
                >
                  <summary className="cursor-pointer font-semibold flex items-center justify-between">
                    {q}
                    <span className="text-[#1677FF] group-open:rotate-45 transition">+</span>
                  </summary>
                  <p className="text-sm text-[#566579] leading-relaxed mt-3">{a}</p>
                </details>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#10233F] text-white">
        <div className="max-w-6xl mx-auto px-6 py-16 text-center">
          <h2 className="text-3xl font-bold mb-3">Not sure which plan fits your operation?</h2>
          <p className="text-[#B8CADD] mb-8 max-w-2xl mx-auto">
            Book a personalized walkthrough. We&apos;ll map your workflows to the right plan and modules.
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/contact" className="px-6 py-3 rounded-full bg-[#1677FF] hover:bg-[#0e5fd6] text-white font-semibold" data-testid="pricing-cta-book-demo">
              Book a Demo
            </Link>
            <Link to="/demo" className="px-6 py-3 rounded-full border border-white/20 hover:border-white/60 text-white transition">
              Experience VyomOS
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
